import React, { useState, useEffect } from 'react';
import { X, Printer, FileText, CreditCard, AlertCircle } from 'lucide-react';

const DaycareChildTransactionHistoryModal = ({ isOpen, onClose, child, apiCall, selectedYear }) => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (isOpen && child) {
      setActiveTab('summary');
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, child, selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Use /charges endpoint — works, no SP dependency
      // academicYear=all to get full picture including paid charges
      const data = await apiCall(`/daycare/charges?familyId=${child.FamilyID}&academicYear=${selectedYear}`);
      setCharges(data || []);
    } catch (err) {
      console.error('Error loading transaction data:', err);
      setCharges([]);
    } finally {
      setLoading(false);
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const getMonthName = (m) =>
    ['January','February','March','April','May','June',
     'July','August','September','October','November','December'][(m||1) - 1] || '';

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ─── Derived totals from charges ──────────────────────────────────────────

  // FinalAmount in DB = Amount (full rate). Discount stored separately.
  const net           = (c) => (c.Amount || 0) - (c.DiscountAmount || 0);
  const activeCharges = charges.filter(c => c.Status !== 'Cancelled');
  const totalCharges  = activeCharges.reduce((s, c) => s + net(c), 0);
  const totalDiscounts= activeCharges.reduce((s, c) => s + (c.DiscountAmount || 0), 0);
  const totalPaid     = activeCharges.reduce((s, c) => s + (c.AmountPaid  || 0), 0);
  const balance       = totalCharges - totalPaid;

  // ─── Transaction list: one row per charge + one row per payment on that charge ──

  const buildTxList = () => {
    const rows = [];
    charges.forEach(c => {
      const isOldBalance = (c.ReferenceID < 0) || c.ChargeType === 'Old Balance';
      const netAmt  = net(c);
      const hasDisc = (c.DiscountAmount || 0) > 0;
      const label   = c.Description ||
        (isOldBalance
          ? `Old Balance — ${getMonthName(c.ChargeMonth)} ${c.ChargeYear}`
          : `Daycare Monthly Charge — ${c.PackageName} — ${child.FirstName} ${child.LastName} — ${getMonthName(c.ChargeMonth)} ${c.ChargeYear}`);

      rows.push({
        date:         c.ChargeDate,
        desc:         label,
        amount:       netAmt,
        discount:     c.DiscountAmount || 0,
        hasDisc,
        fullRate:     c.Amount || 0,
        type:         'charge',
        status:       c.Status,
        isOldBalance,
        sort:         new Date(c.ChargeDate || 0).getTime(),
      });

      if ((c.AmountPaid || 0) > 0) {
        rows.push({
          date:   c.UpdatedAt || c.ChargeDate,
          desc:   'Payment received — Daycare',
          amount: c.AmountPaid,
          type:   'payment',
          status: 'Paid',
          sort:   new Date(c.UpdatedAt || c.ChargeDate || 0).getTime() + 1,
        });
      }
    });
    return rows.sort((a, b) => b.sort - a.sort);
  };

  const txList = buildTxList();

  // ─── Print helpers ─────────────────────────────────────────────────────────

  const statusBadge = (status, isPayment) => {
    const bg    = isPayment ? '#d1fae5' : status === 'Cancelled' ? '#f3f4f6' : status === 'Paid' ? '#d1fae5' : status === 'Partial' ? '#fef3c7' : '#fee2e2';
    const color = isPayment ? '#065f46' : status === 'Cancelled' ? '#6b7280' : status === 'Paid' ? '#065f46' : status === 'Partial' ? '#92400e' : '#991b1b';
    const label = isPayment ? 'Payment' : (status || 'Charge');
    return `<span style="display:inline-block;padding:2px 7px;border-radius:4px;font-size:10px;background:${bg};color:${color}">${label}</span>`;
  };

  const printHeader = (tabLabel) => `
<table width="100%" style="background:#4c3dbf;padding:18px 22px" cellpadding="0" cellspacing="0">
<tr>
  <td style="color:white">
    <div style="font-size:19px;font-weight:bold;margin-bottom:3px">${child.FirstName} ${child.LastName}</div>
    <div style="font-size:11px;opacity:0.85">Family ID: ${child.FamilyID}</div>
    <div style="font-size:11px;opacity:0.85;margin-top:2px">Daycare — Academic Year ${selectedYear} — ${tabLabel}</div>
  </td>
  <td style="text-align:right;color:white;font-size:11px;opacity:0.85;vertical-align:top">
    <div>Collège des Apôtres</div>
    <div>Jounieh, Lebanon</div>
    <div style="margin-top:4px">Statement date: ${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})}</div>
  </td>
</tr>
</table>`;

  const printSummaryRow = () => `
<table width="100%" style="border-bottom:2px solid #e5e7eb" cellpadding="0" cellspacing="0">
<tr>
  <td width="33%" style="padding:13px 18px;border-right:1px solid #e5e7eb">
    <div style="font-size:10px;text-transform:uppercase;color:#6b7280;margin-bottom:4px">Total Charges</div>
    <div style="font-size:20px;font-weight:bold;color:#dc2626">$${totalCharges.toFixed(2)}</div>
    ${totalDiscounts > 0 ? `<div style="font-size:10px;color:#f97316;margin-top:3px">−$${totalDiscounts.toFixed(2)} discounted</div>` : ''}
  </td>
  <td width="33%" style="padding:13px 18px;border-right:1px solid #e5e7eb">
    <div style="font-size:10px;text-transform:uppercase;color:#6b7280;margin-bottom:4px">Total Payments</div>
    <div style="font-size:20px;font-weight:bold;color:#059669">$${totalPaid.toFixed(2)}</div>
  </td>
  <td width="33%" style="padding:13px 18px">
    <div style="font-size:10px;text-transform:uppercase;color:#6b7280;margin-bottom:4px">Balance Due</div>
    <div style="font-size:20px;font-weight:bold;color:${balance > 0 ? '#dc2626' : '#059669'}">$${balance.toFixed(2)}</div>
  </td>
</tr>
</table>`;

  const printFooter = () => `
<table width="100%" style="margin-top:16px;padding-top:10px;border-top:1px solid #e5e7eb" cellpadding="0" cellspacing="0">
<tr>
  <td style="font-size:10px;color:#9ca3af">Collège des Apôtres · Daycare Department · Jounieh, Lebanon</td>
  <td style="text-align:right">
    <div style="font-size:10px;color:#9ca3af">Balance due</div>
    <div style="font-size:13px;font-weight:bold;color:${balance > 0 ? '#dc2626' : '#059669'}">$${balance.toFixed(2)}</div>
  </td>
</tr>
</table>`;

  const buildSummaryContent = () => {
    const rows = txList.map(tx => {
      const cancelled = tx.status === 'Cancelled';
      const isPayment = tx.type === 'payment';
      const amtColor  = isPayment ? '#059669' : cancelled ? '#9ca3af' : '#dc2626';
      const strike    = cancelled ? 'text-decoration:line-through;' : '';
      const sign      = isPayment ? '−' : '+';
      const discNote  = (!isPayment && !cancelled && tx.hasDisc)
        ? `<div style="font-size:10px;color:#f97316;margin-top:2px">−$${tx.discount.toFixed(2)} discount applied</div>` : '';
      return `<tr>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;white-space:nowrap;vertical-align:top">${formatDate(tx.date)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;${strike}vertical-align:top">${tx.desc}${discNote}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;text-align:center;vertical-align:top">${statusBadge(tx.status, isPayment)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;text-align:right;font-size:11px;font-weight:600;color:${amtColor};${strike}vertical-align:top">${sign}$${tx.amount.toFixed(2)}</td>
      </tr>`;
    }).join('');
    return `
<div style="font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;padding:10px 10px 6px;border-bottom:1px solid #f3f4f6">Transaction History</div>
<table width="100%" cellpadding="0" cellspacing="0">
<thead><tr style="background:#f9fafb">
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb;width:11%">Date</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb">Description</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:center;border-bottom:1px solid #e5e7eb;width:13%">Status</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:12%">Amount</th>
</tr></thead>
<tbody>${rows || '<tr><td colspan="4" style="padding:20px;text-align:center;color:#9ca3af">No transactions</td></tr>'}</tbody>
</table>`;
  };

  const buildChargesContent = () => {
    const rows = charges.map(c => {
      const netAmt    = net(c);
      const cancelled = c.Status === 'Cancelled';
      const strike    = cancelled ? 'text-decoration:line-through;' : '';
      const hasDisc   = (c.DiscountAmount || 0) > 0;
      return `<tr>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;${strike}vertical-align:top">${getMonthName(c.ChargeMonth)} ${c.ChargeYear}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#6b7280;${strike}vertical-align:top">${c.PackageName || ''}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:#9ca3af;vertical-align:top">$${(c.Amount||0).toFixed(2)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:#f97316;vertical-align:top">${hasDisc ? `−$${c.DiscountAmount.toFixed(2)}` : '—'}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:${cancelled ? '#9ca3af' : '#dc2626'};font-weight:600;${strike}vertical-align:top">$${netAmt.toFixed(2)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:#059669;font-weight:600;vertical-align:top">$${(c.AmountPaid||0).toFixed(2)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;text-align:center;vertical-align:top">${statusBadge(c.Status, false)}</td>
      </tr>`;
    }).join('');
    return `
<div style="font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;padding:10px 10px 6px;border-bottom:1px solid #f3f4f6">Charges</div>
<table width="100%" cellpadding="0" cellspacing="0">
<thead><tr style="background:#f9fafb">
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb;width:12%">Month</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb">Package</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:11%">Rate</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#f97316;text-align:right;border-bottom:1px solid #e5e7eb;width:11%">Disc</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:11%">Net</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:11%">Paid</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:center;border-bottom:1px solid #e5e7eb;width:11%">Status</th>
</tr></thead>
<tbody>${rows || '<tr><td colspan="5" style="padding:20px;text-align:center;color:#9ca3af">No charges</td></tr>'}</tbody>
</table>`;
  };

  const buildPaymentsContent = () => {
    const paid = txList.filter(t => t.type === 'payment');
    const rows = paid.map(tx => `<tr>
      <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;white-space:nowrap;vertical-align:top">${formatDate(tx.date)}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;vertical-align:top">${tx.desc}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:#059669;font-weight:600;vertical-align:top">−$${tx.amount.toFixed(2)}</td>
    </tr>`).join('');
    const total = paid.reduce((s, t) => s + t.amount, 0);
    return `
<div style="font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;padding:10px 10px 6px;border-bottom:1px solid #f3f4f6">Payments</div>
<table width="100%" cellpadding="0" cellspacing="0">
<thead><tr style="background:#f9fafb">
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb;width:16%">Date</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb">Description</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:15%">Amount</th>
</tr></thead>
<tbody>
  ${rows || '<tr><td colspan="3" style="padding:20px;text-align:center;color:#9ca3af">No payments</td></tr>'}
  ${paid.length > 0 ? `<tr style="background:#f9fafb">
    <td colspan="2" style="padding:8px 10px;font-size:11px;font-weight:bold;color:#374151">Total payments</td>
    <td style="padding:8px 10px;font-size:11px;font-weight:bold;color:#059669;text-align:right">−$${total.toFixed(2)}</td>
  </tr>` : ''}
</tbody>
</table>`;
  };

  const buildEnrollmentsContent = () => {
    const enrollments = [...new Map(charges.map(c => [c.ReferenceID, c])).values()];
    const rows = enrollments.map(c => {
      const enrollCharges = charges.filter(x => x.ReferenceID === c.ReferenceID);
      const activeOnes    = enrollCharges.filter(x => x.Status !== 'Cancelled');
      const total         = activeOnes.reduce((s, x) => s + net(x), 0);
      const paid          = activeOnes.reduce((s, x) => s + (x.AmountPaid  || 0), 0);
      const outstanding   = total - paid;
      return `<tr>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;vertical-align:top">${c.ReferenceID}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;vertical-align:top">${c.PackageName || ''}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:center;vertical-align:top">${activeOnes.length}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:#dc2626;font-weight:600;vertical-align:top">$${total.toFixed(2)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:#059669;font-weight:600;vertical-align:top">$${paid.toFixed(2)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:${outstanding > 0 ? '#dc2626' : '#059669'};font-weight:600;vertical-align:top">$${outstanding.toFixed(2)}</td>
      </tr>`;
    }).join('');
    return `
<div style="font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;padding:10px 10px 6px;border-bottom:1px solid #f3f4f6">Enrollments</div>
<table width="100%" cellpadding="0" cellspacing="0">
<thead><tr style="background:#f9fafb">
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb;width:10%">ID</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:left;border-bottom:1px solid #e5e7eb">Package</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:center;border-bottom:1px solid #e5e7eb;width:10%">Charges</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:14%">Total</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:14%">Paid</th>
  <th style="padding:7px 10px;font-size:10px;font-weight:bold;text-transform:uppercase;color:#6b7280;text-align:right;border-bottom:1px solid #e5e7eb;width:14%">Outstanding</th>
</tr></thead>
<tbody>${rows || '<tr><td colspan="6" style="padding:20px;text-align:center;color:#9ca3af">No enrollments</td></tr>'}</tbody>
</table>`;
  };

  // ─── Print ─────────────────────────────────────────────────────────────────

  const TAB_LABELS = { summary: 'Transaction History', charges: 'Charges', payments: 'Payments', enrollments: 'Enrollments' };

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=900,height=750');

    const tabLabel = TAB_LABELS[activeTab] || 'Statement';

    const contentByTab = {
      summary:     buildSummaryContent(),
      charges:     buildChargesContent(),
      payments:    buildPaymentsContent(),
      enrollments: buildEnrollmentsContent(),
    };

    w.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${tabLabel} — ${child.FirstName} ${child.LastName}</title>
<style>
@page { size: A4 portrait; margin: 16mm 14mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; color:#1f2937; font-size:13px; }
</style>
</head>
<body>
${printHeader(tabLabel)}
${printSummaryRow()}
${contentByTab[activeTab] || ''}
${printFooter()}
</body>
<script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);}</script>
</html>`);
    w.document.close();
  };

  // ─── UI ────────────────────────────────────────────────────────────────────

  if (!isOpen || !child) return null;

  const TABS = ['summary', 'charges', 'payments', 'enrollments'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div style={{ background: '#4c3dbf' }} className="px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{child.FirstName} {child.LastName}</h2>
            <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Family ID: {child.FamilyID} · Year {selectedYear}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || charges.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
              style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }}
            >
              <Printer className="w-4 h-4" />
              Print {TAB_LABELS[activeTab] || 'statement'}
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors ml-1">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-3 divide-x border-b border-gray-200">
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total charges</p>
                <p className="text-2xl font-bold text-red-600">${totalCharges.toFixed(2)}</p>
                {totalDiscounts > 0 && (
                  <p className="text-xs text-orange-500 mt-1">−${totalDiscounts.toFixed(2)} discounted</p>
                )}
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total payments</p>
                <p className="text-2xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Balance due</p>
                <p className={`text-2xl font-bold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ${balance.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-6 flex">
              {TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                    activeTab === tab
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">

              {/* ── Summary Tab ── */}
              {activeTab === 'summary' && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3 border-b border-gray-100">
                    Recent activity
                  </p>
                  {txList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <FileText className="w-10 h-10 mb-3 opacity-40" />
                      <p className="text-sm">No transactions found</p>
                    </div>
                  ) : (
                    txList.map((tx, idx) => {
                      const isPayment    = tx.type === 'payment';
                      const cancelled    = tx.status === 'Cancelled';
                      const isOldBalance = tx.isOldBalance;
                      return (
                        <div key={idx} className="flex items-start gap-3 px-5 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold
                            ${isPayment ? 'bg-green-100 text-green-600' : cancelled ? 'bg-gray-100 text-gray-400' : isOldBalance ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-500'}`}>
                            {isPayment ? '↓' : cancelled ? '✕' : '↑'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`text-sm leading-snug ${cancelled ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                {tx.desc}
                              </p>
                              {isOldBalance && !cancelled && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">old balance</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-gray-400">{formatDate(tx.date)}</p>
                              {tx.hasDisc && !cancelled && (
                                <span className="text-xs text-orange-500 font-medium">
                                  −${tx.discount.toFixed(2)} discount
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`text-sm font-semibold whitespace-nowrap ${
                            isPayment ? 'text-green-600' : cancelled ? 'text-gray-400 line-through' : isOldBalance ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {isPayment ? '−' : '+'}${tx.amount.toFixed(2)}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* ── Charges Tab ── */}
              {activeTab === 'charges' && (
                charges.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <AlertCircle className="w-10 h-10 mb-3 opacity-40" />
                    <p className="text-sm">No charges found</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Month</th>
                        <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Package</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rate</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-orange-400 uppercase tracking-wide">Disc</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Net</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Paid</th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {charges.map((c, idx) => {
                        const netAmt       = net(c);
                        const cancelled    = c.Status === 'Cancelled';
                        const hasDisc      = (c.DiscountAmount || 0) > 0;
                        const isOldBalance = (c.ReferenceID < 0) || c.ChargeType === 'Old Balance';
                        return (
                          <tr key={idx} className={`hover:bg-gray-50 ${isOldBalance ? 'bg-amber-50' : ''}`}>
                            <td className={`px-5 py-3 ${cancelled ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                              {getMonthName(c.ChargeMonth)} {c.ChargeYear}
                            </td>
                            <td className={`px-5 py-3 text-xs ${cancelled ? 'text-gray-400' : 'text-gray-500'}`}>
                              {isOldBalance
                                ? <span className="inline-flex items-center gap-1">{c.PackageName || 'Old Balance'} <span className="bg-amber-100 text-amber-700 px-1 py-0.5 rounded">old</span></span>
                                : c.PackageName
                              }
                            </td>
                            <td className={`px-4 py-3 text-right text-gray-400 text-xs ${cancelled ? 'line-through' : ''}`}>
                              ${(c.Amount || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-xs text-orange-500 font-medium">
                              {hasDisc ? `−$${c.DiscountAmount.toFixed(2)}` : <span className="text-gray-300">—</span>}
                            </td>
                            <td className={`px-4 py-3 text-right font-semibold ${cancelled ? 'text-gray-400 line-through' : isOldBalance ? 'text-amber-600' : 'text-red-600'}`}>
                              ${netAmt.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-green-600 font-medium">
                              ${(c.AmountPaid || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                c.Status === 'Paid'      ? 'bg-green-100 text-green-800' :
                                c.Status === 'Partial'   ? 'bg-yellow-100 text-yellow-800' :
                                c.Status === 'Cancelled' ? 'bg-gray-100 text-gray-500' :
                                c.Status === 'Frozen'    ? 'bg-indigo-100 text-indigo-800' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {c.Status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              )}

              {/* ── Payments Tab ── */}
              {activeTab === 'payments' && (
                (() => {
                  const paid = txList.filter(t => t.type === 'payment');
                  return paid.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <CreditCard className="w-10 h-10 mb-3 opacity-40" />
                      <p className="text-sm">No payments recorded</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                          <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                          <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paid.map((tx, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{formatDate(tx.date)}</td>
                            <td className="px-5 py-3 text-gray-700">{tx.desc}</td>
                            <td className="px-5 py-3 text-right font-semibold text-green-600">−${tx.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()
              )}

              {/* ── Enrollments Tab ── */}
              {activeTab === 'enrollments' && (
                <div className="p-5 space-y-3">
                  {charges.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">No enrollments found</p>
                  ) : (
                    [...new Map(charges.map(c => [c.ReferenceID, c])).values()].map((c, idx) => {
                      const enrollCharges = charges.filter(x => x.ReferenceID === c.ReferenceID);
                      const activeOnes    = enrollCharges.filter(x => x.Status !== 'Cancelled');
                      const total         = activeOnes.reduce((s, x) => s + net(x), 0);
                      const paid          = activeOnes.reduce((s, x) => s + (x.AmountPaid  || 0), 0);
                      return (
                        <div key={idx} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{c.PackageName}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Enrollment ID: {c.ReferenceID}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-700">${total.toFixed(2)}</p>
                            <p className="text-xs text-green-600 mt-0.5">Paid: ${paid.toFixed(2)}</p>
                            <p className="text-xs text-gray-400">{activeOnes.length} charges</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
              <span>{charges.length} charges</span>
              <span className={`font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                Balance: ${balance.toFixed(2)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DaycareChildTransactionHistoryModal;
